import React, { useMemo } from 'react';
import { Div, Icon } from '@pinpoint/design-kit';

import { Rbac } from 'src/containers';
import CONSTANTS from 'src/store/constants';
import useScoringInProgress from 'src/hooks/useScoringInProgress';

/* eslint-disable react/prop-types */

const hasDownloadableLink = (downloads) => downloads && downloads.some(({ bucket, key }) => bucket && key);

const isDownloadActivated = (downloads, filter, client) => {
  const isCluster = filter && filter.startsWith('CLUSTERING');
  const isActivated = !isCluster || (isCluster && client && client.hasActivatedClusters);
  const hasLink = hasDownloadableLink(downloads);
  return isActivated && hasLink;
};

const getDownloadTooltip = (client, dataset, filter, payload, tag, hasScoringInProgress) => {
  if (!hasDownloadableLink(payload) && !hasScoringInProgress) {
    return 'No available downloads';
  }

  if ([CONSTANTS.SEGMENT.TAG.VALIDATION, CONSTANTS.SEGMENT.TAG.ACTUARIAL, 'ACTVAL'].includes(tag)) {
    return 'Download validation predictions';
  }

  if (tag === CONSTANTS.SEGMENT.TAG.TARGET) {
    return 'Download predictions';
  }

  return 'Download scores';
};

export function DeleteComponent({ iconSize, type, onOpenModal }) {
  return (
    <Rbac
      roles={[CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN]}
      fallback={
        <Icon
          size={iconSize}
          viewBox="0 0 50 50"
          color="tabRest"
          name="Delete"
          cursor="not-allowed"
          m={{ r: '0.3125rem' }}
        />
      }
    >
      <Icon
        size={iconSize}
        color="tabSelected"
        hoverColor="danger800"
        viewBox="0 0 50 50"
        name="Delete"
        cursor="pointer"
        data-tip={`Delete ${type}`}
        data-test="delete"
        hoverTransform="scale(1.25)"
        m={{ r: '0.3125rem' }}
        onClick={onOpenModal}
      />
    </Rbac>
  );
}

export function DownloadComponent({
  id,
  client,
  dataset,
  downloads,
  filter,
  iconSize,
  isSandboxViewer,
  onOpenModal,
  statuses = [],
  tag,
}) {
  const { hasScoringInProgress, loading } = useScoringInProgress(id, statuses);
  const hasDownloads = useMemo(() => isDownloadActivated(downloads, filter, client), [downloads, filter, client]);
  // Hide the download button when there's nothing to show
  if (!hasDownloads && !hasScoringInProgress) {
    return null;
  }
  // Disable the download button in the sandbox viewer tenant + role pair
  if (isSandboxViewer) {
    return (
      <Div d="flex">
        <Icon
          size={iconSize}
          viewBox="0 0 50 50"
          color="tabRest"
          name="Download"
          cursor="not-allowed"
          data-tip="Download Disabled"
          m={{ r: '0.3125rem' }}
        />
      </Div>
    );
  }

  if (!hasScoringInProgress && loading) {
    return <Icon color="tabSelected" name="Loading" size={iconSize} />;
  }

  return (
    <Rbac roles={[CONSTANTS.ROLE.ADMIN, CONSTANTS.ROLE.SUPER_ADMIN, CONSTANTS.ROLE.VIEWER]}>
      {hasDownloads && (
        <Icon
          color="tabSelected"
          viewBox="0 0 50 50"
          name="Download"
          hoverTransform="scale(1.25)"
          size={iconSize}
          cursor="pointer"
          data-tip={getDownloadTooltip(client, dataset, filter, downloads, tag)}
          data-test="download"
          m={{ r: '0.3125rem' }}
          onClick={onOpenModal}
        />
      )}
      {hasScoringInProgress && (
        <Icon
          color="tabSelected"
          viewBox="0 0 100 100"
          name="InProgress"
          hoverTransform={!hasDownloads ? 'scale(1.25)' : 'scale(1)'}
          size={iconSize}
          cursor={!hasDownloads ? 'pointer' : null}
          data-tip="Scoring in progress"
          data-test="scoring-in-progress"
          m={{ r: '0.3125rem' }}
          onClick={!hasDownloads ? onOpenModal : null}
        />
      )}
    </Rbac>
  );
}
